<script setup>
import { ref, computed, toRef, watch } from 'vue';
import { useAlert } from 'dashboard/composables';
import { useFunctionGetter, useStore } from 'dashboard/composables/store';
import {
  COMPONENT_TYPES,
  MEDIA_FORMATS,
  findComponentByType,
} from 'dashboard/helper/templateHelper';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  inboxId: {
    type: Number,
    default: undefined,
  },
  selectedTemplateId: {
    type: [String, Number],
    default: '',
  },
});

const emit = defineEmits(['onSelect']);

const { t } = useI18n();
const store = useStore();
const query = ref('');
const selectedTemplateId = ref('');
const isRefreshing = ref(false);

const whatsAppTemplateMessages = useFunctionGetter(
  'inboxes/getFilteredWhatsAppTemplates',
  toRef(props, 'inboxId')
);

const templateId = template => String(template.id || template.name || '');

const filteredTemplateMessages = computed(() =>
  whatsAppTemplateMessages.value.filter(template =>
    template.name.toLowerCase().includes(query.value.toLowerCase())
  )
);

const selectedTemplate = computed(() =>
  filteredTemplateMessages.value.find(
    template => templateId(template) === selectedTemplateId.value
  )
);

const getTemplateBody = template => {
  return findComponentByType(template, COMPONENT_TYPES.BODY)?.text || '';
};

const getTemplateHeader = template => {
  return findComponentByType(template, COMPONENT_TYPES.HEADER);
};

const getTemplateButtons = template => {
  return findComponentByType(template, COMPONENT_TYPES.BUTTONS);
};

const getSummary = template => {
  const header = getTemplateHeader(template);
  const body = getTemplateBody(template);

  if (header?.format === 'TEXT' && header.text) {
    return header.text;
  }

  if (header && MEDIA_FORMATS.includes(header.format)) {
    return `${header.format} + ${body}`;
  }

  return body;
};

const selectTemplate = template => {
  if (!template) return;
  selectedTemplateId.value = templateId(template);
  emit('onSelect', template);
};

const onTemplateChange = () => {
  selectTemplate(selectedTemplate.value);
};

const refreshTemplates = async () => {
  isRefreshing.value = true;
  try {
    await store.dispatch('inboxes/syncTemplates', props.inboxId);
    useAlert(t('WHATSAPP_TEMPLATES.PICKER.REFRESH_SUCCESS'));
  } catch (error) {
    useAlert(t('WHATSAPP_TEMPLATES.PICKER.REFRESH_ERROR'));
  } finally {
    isRefreshing.value = false;
  }
};

watch(
  filteredTemplateMessages,
  templates => {
    if (!templates.length) {
      selectedTemplateId.value = '';
      return;
    }

    const incomingSelectedId = String(props.selectedTemplateId || '');
    const preferredId = incomingSelectedId || selectedTemplateId.value;
    const existingTemplate = templates.find(
      template => templateId(template) === preferredId
    );

    selectTemplate(existingTemplate || templates[0]);
  },
  { immediate: true }
);

watch(
  () => props.selectedTemplateId,
  id => {
    if (id) selectedTemplateId.value = String(id);
  },
  { immediate: true }
);
</script>

<template>
  <div class="flex flex-col gap-3">
    <div class="flex gap-2">
      <div
        class="flex flex-1 gap-1 items-center px-2.5 py-0 rounded-md bg-n-alpha-2 border border-solid border-n-weak focus-within:border-n-brand"
      >
        <fluent-icon icon="search" class="text-n-slate-11" size="16" />
        <input
          v-model="query"
          type="search"
          :placeholder="t('WHATSAPP_TEMPLATES.PICKER.SEARCH_PLACEHOLDER')"
          class="reset-base w-full h-9 bg-transparent text-n-slate-12 !text-sm !outline-0"
        />
      </div>
      <button
        :disabled="isRefreshing"
        class="flex justify-center items-center w-9 h-9 rounded-md bg-n-alpha-2 border border-solid border-n-weak hover:bg-n-alpha-3 disabled:opacity-50 disabled:cursor-not-allowed"
        :title="t('WHATSAPP_TEMPLATES.PICKER.REFRESH_BUTTON')"
        @click="refreshTemplates"
      >
        <Icon
          icon="i-lucide-refresh-ccw"
          class="text-n-slate-12 size-4"
          :class="{ 'animate-spin': isRefreshing }"
        />
      </button>
    </div>

    <label class="flex flex-col gap-1.5">
      <span class="text-xs font-medium text-n-slate-11">
        Template aprovado
      </span>
      <select
        v-model="selectedTemplateId"
        class="h-10 rounded-md border border-solid border-n-weak bg-n-alpha-2 px-3 text-sm text-n-slate-12 outline-none focus:border-n-brand"
        @change="onTemplateChange"
      >
        <option value="" disabled>Selecione um template</option>
        <option
          v-for="template in filteredTemplateMessages"
          :key="templateId(template)"
          :value="templateId(template)"
        >
          {{ template.name }}
        </option>
      </select>
    </label>

    <div class="text-xs text-n-slate-11">
      {{ filteredTemplateMessages.length }} templates aprovados disponíveis
    </div>

    <div v-if="selectedTemplate" class="rounded-md bg-n-alpha-2 px-3 py-2">
      <div class="flex flex-wrap gap-2 text-xs text-n-slate-10">
        <span>{{ selectedTemplate.category }}</span>
        <span>{{ selectedTemplate.language }}</span>
        <span v-if="getTemplateButtons(selectedTemplate)?.buttons?.length">
          {{ getTemplateButtons(selectedTemplate).buttons.length }} botões
        </span>
      </div>
    </div>

    <div v-if="!filteredTemplateMessages.length" class="py-6 text-center">
      <div v-if="query && whatsAppTemplateMessages.length">
        <p>
          {{ t('WHATSAPP_TEMPLATES.PICKER.NO_TEMPLATES_FOUND') }}
          <strong>{{ query }}</strong>
        </p>
      </div>
      <div v-else-if="!whatsAppTemplateMessages.length" class="space-y-4">
        <p class="text-n-slate-11">
          {{ t('WHATSAPP_TEMPLATES.PICKER.NO_TEMPLATES_AVAILABLE') }}
        </p>
      </div>
    </div>
  </div>
</template>
